import { useCallback, useEffect, useRef } from 'react';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';

/** CSS selector for all focusable elements within the editor surface.
 *
 * Elements can opt out of the trap by setting `data-a11y-skip-focus-trap="true"`
 * — useful for affordances like the drawer's resize handle that need to be
 * keyboard-operable when directly focused but shouldn't be part of the Tab
 * cycle or claim initial focus.
 */
export const FOCUSABLE_SELECTOR =
  'button:not([disabled]):not([data-a11y-skip-focus-trap]), [href]:not([data-a11y-skip-focus-trap]), input:not([disabled]):not([data-a11y-skip-focus-trap]), select:not([disabled]):not([data-a11y-skip-focus-trap]), textarea:not([disabled]):not([data-a11y-skip-focus-trap]), [tabindex]:not([tabindex="-1"]):not([data-a11y-skip-focus-trap])';

/**
 * Shared focus-management for the node editor surface (modal or drawer).
 *
 * Encapsulates the proven accessibility behavior originally implemented inline
 * in `NodeEditorModal`:
 *  - Escape closes the editor (suppressed while the connection picker is open,
 *    so the picker's own Escape handling wins — layered dismissal).
 *  - Tab / Shift+Tab focus trap cycling within the container (modal only).
 *  - Backdrop click closes the editor (callers that show a backdrop wire this
 *    to their scrim; the non-modal drawer no longer shows one).
 *  - Focus save on open / restore on close.
 *  - `focusin` redirect that pulls stray focus back inside the container
 *    (modal only), while allowlisting Fluent portal surfaces
 *    (listbox/menu/dialog/tooltip) and the assistant chat sidebar
 *    (`[data-chat-sidebar]`).
 *
 * Pass `{ modal: false }` for non-modal shells (e.g. the side-anchored
 * `NodeEditorDrawer`). Non-modal mode keeps Escape + focus save/restore but
 * disables the Tab trap and the focus-redirect so users can Tab back onto the
 * canvas — matching the semantics of a side panel that coexists with the
 * workflow behind it.
 */
export function useNodeEditorFocusTrap<T extends HTMLElement = HTMLDivElement>(
  options: { modal?: boolean } = {},
) {
  const { modal = true } = options;
  const containerRef = useRef<T>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const isClosingRef = useRef(false);

  const isOpen = useNodeEditorStore((s) => s.isOpen);
  const closeEditor = useNodeEditorStore((s) => s.closeEditor);
  const connectionPickerOpen = useNodeEditorStore((s) => s.connectionPickerOpen);
  const closeConnectionPicker = useNodeEditorStore((s) => s.closeConnectionPicker);

  // Escape + Tab focus-trap cycling.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Layered dismissal: while the connection picker is open, Escape
        // collapses it back to the summary instead of closing the whole editor.
        if (connectionPickerOpen) {
          e.stopPropagation();
          closeConnectionPicker();
          return;
        }
        e.stopPropagation();
        void closeEditor();
        return;
      }

      // Only modal shells trap Tab. The drawer intentionally allows Tab to
      // escape back to the canvas so users can weave between the side panel
      // and the workflow — the whole point of a non-modal surface.
      if (modal && e.key === 'Tab' && containerRef.current) {
        const focusable = containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusable.length === 0) return;

        const focusableElements = Array.from(focusable);
        const activeIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
        const direction = e.shiftKey ? -1 : 1;
        const fallbackIndex = e.shiftKey ? focusableElements.length - 1 : 0;
        const nextIndex =
          activeIndex === -1
            ? fallbackIndex
            : (activeIndex + direction + focusableElements.length) % focusableElements.length;

        e.preventDefault();
        e.stopPropagation();
        focusableElements[nextIndex]?.focus();
      }
    },
    [closeEditor, connectionPickerOpen, closeConnectionPicker, modal],
  );

  // Backdrop click closes (only when the click lands on the backdrop itself).
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        void closeEditor();
      }
    },
    [closeEditor],
  );

  // Save focus on open, restore it on close, and focus the first focusable
  // element inside the container. Both the modal and the drawer stay mounted
  // and only toggle `isOpen`, so restoration is done in the effect cleanup,
  // which runs when `isOpen` flips to `false` (and also on unmount). An `else`
  // branch here would be redundant with that cleanup.
  useEffect(() => {
    if (!isOpen) return undefined;

    isClosingRef.current = false;
    if (document.activeElement instanceof HTMLElement) {
      previousFocusRef.current = document.activeElement;
    }
    const rafId = requestAnimationFrame(() => {
      if (containerRef.current) {
        const firstFocusable = containerRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        (firstFocusable ?? containerRef.current).focus();
      }
    });

    return () => {
      cancelAnimationFrame(rafId);
      isClosingRef.current = true;
      if (previousFocusRef.current?.isConnected) {
        previousFocusRef.current.focus();
      }
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  // Redirect any focus that lands outside the container back inside.
  // Modal-only — the drawer intentionally allows focus to leave (non-modal
  // side-panel semantics).
  useEffect(() => {
    if (!modal || !isOpen) return;

    const handleFocusIn = (e: FocusEvent) => {
      if (isClosingRef.current) return;
      const target = e.target as HTMLElement;
      if (!containerRef.current) return;
      if (containerRef.current.contains(target)) return;
      // Allow focus in Fluent UI portal surfaces (dropdowns, menus, popovers).
      if (target.closest?.('[role="listbox"], [role="menu"], [role="dialog"], [role="alertdialog"], [role="tooltip"]')) return;
      // Allow focus in the chat sidebar (assistant panel lives outside the editor).
      if (target.closest?.('[data-chat-sidebar]')) return;
      e.stopPropagation();
      const firstFocusable = containerRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (firstFocusable ?? containerRef.current).focus();
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [isOpen, modal]);

  return { containerRef, handleKeyDown, handleBackdropClick };
}
